import {
  Button,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { RiAddLine } from "react-icons/ri";
import { SearchableMenuPopover } from "src/components/Popovers/SearchableMenuPopover";
import {
  getAvailableOfferingOptions,
  groupOfferingOptionsByCategory,
  MAX_OPTIONS_IN_OFFERING,
  offeringKey,
  type OfferingOption,
  type SchoolOffering,
} from "../offerings";
import { AddOfferingDrawer } from "./AddOfferingDrawer";

type OfferingOptionItem = {
  key: string;
  label: string;
};

type Props = {
  options: OfferingOption[];
  existingOfferings: SchoolOffering[];
  onAddOffering: (optionIds: string[]) => void;
  onGoToOfferingCategories: () => void;
};

export const AddOfferingButton: React.FC<Props> = ({
  options,
  existingOfferings,
  onAddOffering,
  onGoToOfferingCategories,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const existingOfferingKeys = useMemo(
    () =>
      new Set(
        existingOfferings.map((offering) =>
          offeringKey(offering.options.map((option) => option.id))
        )
      ),
    [existingOfferings]
  );
  const categoryCount = new Set(options.map((option) => option.categoryId))
    .size;

  const button = (
    <Button
      size="sm"
      variant="outline"
      colorScheme="gray"
      leftIcon={<RiAddLine />}
      width="fit-content"
      borderStyle="dashed"
      borderColor="gray.300"
    >
      Add offering
    </Button>
  );

  if (categoryCount === 0) {
    return (
      <Popover placement="bottom-start" gutter={0} isLazy>
        <PopoverTrigger>{button}</PopoverTrigger>
        <PopoverContent p={4}>
          <Text fontSize="sm">
            No offering category options configured.{" "}
            <Link
              as="button"
              type="button"
              color="primary.500"
              onClick={onGoToOfferingCategories}
            >
              Create it first
            </Link>{" "}
            and then come back to select it.
          </Text>
        </PopoverContent>
      </Popover>
    );
  }

  if (categoryCount === 1) {
    const availableOptions = getAvailableOfferingOptions({
      options,
      selectedOptionIds: [null],
      slotIndex: 0,
      totalNumSlots: 1,
      existingOfferingKeys,
    });

    return (
      <SearchableMenuPopover<OfferingOptionItem>
        items={groupOfferingOptionsByCategory(availableOptions)}
        popoverTrigger={button}
        searchPlaceholder="Search for options"
        popoverProps={{ gutter: 0 }}
        onItemSelected={(option) => {
          onAddOffering([option.key]);
        }}
      />
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        colorScheme="gray"
        leftIcon={<RiAddLine />}
        width="fit-content"
        borderStyle="dashed"
        borderColor="gray.300"
        onClick={onOpen}
      >
        Add offering
      </Button>
      <AddOfferingDrawer
        isOpen={isOpen}
        options={options}
        existingOfferingKeys={existingOfferingKeys}
        totalNumSlots={Math.min(MAX_OPTIONS_IN_OFFERING, categoryCount)}
        onClose={onClose}
        onAddOffering={onAddOffering}
        onGoToOfferingCategories={onGoToOfferingCategories}
      />
    </>
  );
};
